import { z } from "zod";
import { API_PREFIX, listQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { actorSchema, entityTypeSchema } from "./common.ts";

/**
 * Files API — /api/v1/files (perms `files:read` / `files:write`; attaching to a
 * record also requires read access to that record).
 * Storage is content-addressed (sha256) behind a storage adapter (local disk in
 * dev, S3-compatible in prod); every upload still creates its own file row.
 */

export const FILE_VISIBILITIES = ["internal", "client"] as const;
export type FileVisibility = (typeof FILE_VISIBILITIES)[number];
/** `client` files are visible in the client portal for the attached record. */
export const fileVisibilitySchema = z.enum(FILE_VISIBILITIES);

/** Multipart field that carries the binary. */
export const FILE_UPLOAD_FIELD = "file";
/** Server-side limit (bytes). Larger → 413 PAYLOAD_TOO_LARGE. */
export const FILE_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;
/** Executable / script extensions rejected with 415 UNSUPPORTED_MEDIA_TYPE. */
export const BLOCKED_FILE_EXTENSIONS = [
  "exe", "msi", "bat", "cmd", "com", "scr", "pif", "cpl", "dll", "sys", "vbs", "vbe", "js", "jse", "wsf", "wsh",
  "ps1", "psm1", "sh", "jar", "app", "lnk", "hta", "reg", "msc", "gadget",
] as const;
export const SHARE_LINK_DEFAULT_MINUTES = 60 * 24;
export const SHARE_LINK_MAX_MINUTES = 60 * 24 * 30;

/**
 * File DTO. `url` is the authenticated download path (`/api/v1/files/:id/download`);
 * portal routes expose their own path. `previewable` = safe to render inline.
 */
/**
 * Where a file is in the virus check.
 *
 * `skipped` is deliberately distinct from `clean`: it means no scanner was
 * configured, and "we do not know" must never render to a person as "safe".
 */
export const FILE_SCAN_STATUSES = ["pending", "clean", "infected", "skipped", "error"] as const;
export type FileScanStatus = (typeof FILE_SCAN_STATUSES)[number];
export const fileScanStatusSchema = z.enum(FILE_SCAN_STATUSES);

export const fileSchema = z.object({
  id: uuidSchema,
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  /** Lowercase hex SHA-256 of the content. */
  sha256: z.string(),
  entityType: entityTypeSchema.nullable(),
  entityId: uuidSchema.nullable(),
  visibility: fileVisibilitySchema,
  uploadedBy: actorSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  url: z.string(),
  previewable: z.boolean(),
  /** Where the virus check got to. Only `clean` and `skipped` can be downloaded. */
  scanStatus: fileScanStatusSchema,
  /** What the engine found, when it found something. */
  scanThreat: z.string().nullable(),
});
export type FileDto = z.infer<typeof fileSchema>;

/** A user-supplied filename: 1–255 chars, no path separators or control characters. */
export const filenameSchema = z
  .string()
  .trim()
  .min(1)
  .max(255)
  .refine((n) => !/[\\/\u0000-\u001f\u007f]/.test(n) && n !== "." && n !== "..", "Invalid file name");

/**
 * POST /api/v1/files — multipart/form-data: field "file" (exactly one) + these
 * text fields → 201 FileDto. `entityType` and `entityId` go together.
 */
export const uploadFileFieldsSchema = z
  .object({
    entityType: entityTypeSchema.optional(),
    entityId: uuidSchema.optional(),
    visibility: fileVisibilitySchema.default("internal"),
  })
  .strict()
  .refine((o) => !!o.entityType === !!o.entityId, { message: "entityType and entityId must be provided together", path: ["entityId"] });
export type UploadFileFields = z.input<typeof uploadFileFieldsSchema>;

/**
 * GET /api/v1/files?entityType&entityId&visibility&q&limit&offset&sort&order → Page<FileDto>.
 * sort ∈ createdAt | filename | sizeBytes (default createdAt desc).
 */
export const listFilesQuerySchema = listQuerySchema
  .extend({
    entityType: entityTypeSchema.optional(),
    entityId: uuidSchema.optional(),
    visibility: fileVisibilitySchema.optional(),
  })
  .refine((o) => !o.entityId || !!o.entityType, { message: "entityId requires entityType", path: ["entityId"] });
export type ListFilesQuery = z.infer<typeof listFilesQuerySchema>;

/** GET /api/v1/files/:id → FileDto. */

/**
 * GET /api/v1/files/:id/download?disposition= → binary stream with
 * Content-Type, Content-Length, ETag (sha256) and Content-Disposition.
 * `inline` is honoured only for previewable types (else forced to attachment);
 * responses carry `X-Content-Type-Options: nosniff` and a sandboxing CSP.
 */
export const downloadFileQuerySchema = z.object({
  disposition: z.enum(["attachment", "inline"]).default("attachment"),
});

/**
 * POST /api/v1/files/:id/share-link (perm `files:write`) → ShareLink.
 * Body optional (defaults to 24 h). The URL is absolute, signed (HMAC) and
 * time-limited; it serves the file without authentication.
 */
export const createShareLinkInputSchema = z
  .object({
    expiresInMinutes: z.number().int().min(5).max(SHARE_LINK_MAX_MINUTES).default(SHARE_LINK_DEFAULT_MINUTES),
  })
  .strict()
  .prefault({});
export const shareLinkSchema = z.object({ url: z.string(), expiresAt: timestampSchema });
export type ShareLink = z.infer<typeof shareLinkSchema>;

/**
 * Public, unauthenticated: GET /api/v1/files/:id/shared?expires=&sig= → stream.
 * `expires` = unix seconds; `sig` = base64url HMAC-SHA256 of `${id}.${expires}`.
 * Expired / bad signature → 404 NOT_FOUND (never reveal existence).
 */
export const sharedFileQuerySchema = z.object({
  expires: z.coerce.number().int().positive(),
  sig: z.string().regex(/^[A-Za-z0-9_-]{16,128}$/),
});

/** PATCH /api/v1/files/:id (perm `files:write`) → FileDto. */
export const updateFileInputSchema = z
  .object({ filename: filenameSchema, visibility: fileVisibilitySchema })
  .partial()
  .strict()
  .refine((o) => Object.keys(o).length > 0, "Nothing to update");
export type UpdateFileInput = z.input<typeof updateFileInputSchema>;

/** DELETE /api/v1/files/:id (perm `files:write`) → 204 (soft delete; blob GC'd when unreferenced). */

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export function fileDownloadPath(id: string): string {
  return `${API_PREFIX}/files/${id}/download`;
}

/** Lowercase extension without the dot ("" when none). */
export function fileExtension(filename: string): string {
  const base = filename.split(/[\\/]/).pop() ?? "";
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : "";
}

export function isBlockedFilename(filename: string): boolean {
  return (BLOCKED_FILE_EXTENSIONS as readonly string[]).includes(fileExtension(filename));
}

/**
 * Makes an uploaded filename safe: drops any path, control and reserved
 * characters, trims dots/spaces, caps at 255 chars keeping the extension.
 */
export function sanitizeFilename(input: string): string {
  const base = (input.split(/[\\/]/).pop() ?? "")
    .replace(/[\u0000-\u001f\u007f<>:"|?*]/g, "")
    .replace(/\s+/g, " ")
    .replace(/^[\s.]+|[\s.]+$/g, "");
  if (!base) return "file";
  if (base.length <= 255) return base;
  const ext = fileExtension(base);
  const keep = ext && ext.length < 20 ? `.${ext}` : "";
  return base.slice(0, 255 - keep.length) + keep;
}

const PREVIEWABLE = [
  /^image\/(png|jpeg|gif|webp|avif|svg\+xml)$/,
  /^application\/pdf$/,
  /^text\/(plain|csv)$/,
  /^video\/(mp4|webm)$/,
  /^audio\/(mpeg|ogg|wav|webm|mp4)$/,
];
/** Types the web can render inline (img/iframe/video/audio). */
export function isPreviewableContentType(contentType: string): boolean {
  const ct = (contentType.split(";")[0] ?? "").trim().toLowerCase();
  return PREVIEWABLE.some((re) => re.test(ct));
}

/** Human size, e.g. 1536 → "1.5 KB". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  const digits = i === 0 || v >= 100 ? 0 : 1;
  return `${Number(v.toFixed(digits))} ${units[i]}`;
}
